import type { BankFeedAccountType } from 'domain/bankFeed/schemas';

/**
 * Hierarchical query keys for the bank-feed domain.
 *
 * The factory shape lets consumers invalidate at any level:
 *   - `bankFeedKeys.all`                  — invalidate every bank-feed query
 *   - `bankFeedKeys.connections(companyId)`   — invalidate one company's connections list
 *   - `bankFeedKeys.syncSchedules(companyId)` — invalidate one company's settings
 *
 * Always invalidate via this factory's value, never a hand-typed string literal:
 * typos won't lint-fail but will silently break invalidation.
 */
export const bankFeedKeys = {
  all: ['bankFeed'] as const,
  syncSchedules: (companyId: string) => [...bankFeedKeys.all, 'syncSchedules', companyId] as const,
  providers: () => [...bankFeedKeys.all, 'providers'] as const,
  userPermissions: (companyId: string) => [...bankFeedKeys.all, 'userPermissions', companyId] as const,
  connections: (companyId: string) => [...bankFeedKeys.all, 'connections', companyId] as const,
  erpAccounts: (currency: string) => [...bankFeedKeys.all, 'erpAccounts', currency] as const,
  transactionsHeaders: (accountType: BankFeedAccountType) =>
    [...bankFeedKeys.all, 'transactionsHeaders', accountType] as const,
  transactionsSummary: () => [...bankFeedKeys.all, 'transactionsSummary'] as const,
  transactionsAutocomplete: (
    field: string,
    accountType: BankFeedAccountType,
    currency: string,
    accountIds: string[],
  ) =>
    [...bankFeedKeys.all, 'transactionsAutocomplete', field, accountType, currency, accountIds] as const,
};
